import React, { useEffect } from "react";
// ANT DESIGN COMPONENTS
import { Button, Tooltip, Select } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRYARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faFilterCircleXmark,
	faSearch,
	faFilter,
	faDownload,
	faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
	updateReportRefreshTable,
	updateOpenDrawerFilters,
	updateFilterLiftType,
	updateFilterExclusionDateRange,
	updateFilterExclusionTimeRange,
	updateFilterExclusionSaturdays,
	updateFilterExclusionSundays,
	updateFilterExclusionHolidays,
	updateFilterCascaderTypeLevel,
	updateFilterCascaderTypeArr,
	updateFilterCascaderTypeList,
	updateFilterCascaderValue,
	updateReportResetFilters,
	updateOpenAlertDownloadReport,
	updateFilterUseCustomSearch,
	updateFilterType,
} from "../../redux/reports/reportsSlice";
// COMPONENTS
import ReportsFilterStartDate from "./ReportsFilterStartDate";
import ReportsFilterEndDate from "./ReportsFilterEndDate";
import ReportsFilterGetBanks from "./ReportsFilterGetBanks";
import ReportsFilterGetServers from "./ReportsFilterGetServers";
import ReportsFilterGetOrganizations from "./ReportsFilterGetOrganizations";
import ReportsFilterGetLifts from "./ReportsFilterGetLifts";
// INTERFACES
import { RootState } from "../../app/store";
import { ReportsHeaderInterface } from "../../interfaces/Reports.interface";

function ReportsHeader({
	level,
	downloadReport,
	getDataWhenOpen,
	infoDescriptionText,
	showDatePicker,
}: ReportsHeaderInterface) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");
	const { Option } = Select;

	// ************************************************ */
	// USE STATE VARIABLES **************************** */

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const {
		filterCascaderTypeLevel,
		filterCascaderTypeArr,
		filterStartDate,
		filterEndDate,
		filterCascaderValue,
		tableSelectedColumns,
		filterUseCustomSearch,
		filterType,
	} = useSelector((state: RootState) => state.report);
	// ************************************************ */
	// SERVICES AND API CALLS ************************* */

	// ************************************************ */
	// FUNCTIONS ************************************** */

	const onClickResetFilters = () => {
		dispatch(updateFilterLiftType("ALL"));
		dispatch(updateFilterExclusionDateRange([]));
		dispatch(updateFilterExclusionTimeRange([]));
		dispatch(updateFilterExclusionSaturdays(false));
		dispatch(updateFilterExclusionSundays(false));
		dispatch(updateFilterExclusionHolidays(false));
		dispatch(updateFilterCascaderTypeLevel(level));
		dispatch(updateFilterCascaderTypeArr([]));
		dispatch(updateFilterCascaderTypeList([]));
		dispatch(updateFilterCascaderValue([]));
		//
		dispatch(updateReportResetFilters(true));
		setTimeout(() => {
			dispatch(updateReportResetFilters(false));
		}, 100);
	};

	const onClickReloadPage = async () => {
		dispatch(updateReportRefreshTable(true));
		setTimeout(() => {
			dispatch(updateReportRefreshTable(false));
		}, 1000);
	};

	const disableSearchAndRefreshButton = () => {
		let disableButton = false;
		if (
			(!filterUseCustomSearch.useCustomSearch
				? filterCascaderTypeLevel === 0 ||
				  filterCascaderTypeArr.length === 0 ||
				  filterCascaderValue.length === 0
				: filterUseCustomSearch.value === "" ||
				  filterUseCustomSearch.type === "") ||
			filterStartDate === "" ||
			filterEndDate === ""
		) {
			disableButton = true;
		}
		return disableButton;
	};

	const disableDownloadButton = () => {
		let disableButton = false;
		if (
			(!filterUseCustomSearch.useCustomSearch
				? filterCascaderTypeLevel === 0 ||
				  filterCascaderTypeArr.length === 0 ||
				  filterCascaderValue.length === 0
				: filterUseCustomSearch.value === "" ||
				  filterUseCustomSearch.type === "") ||
			filterStartDate === "" ||
			filterEndDate === "" ||
			tableSelectedColumns.length === 0
		) {
			disableButton = true;
		}
		return disableButton;
	};

	const onClickDownloadReport = () => {
		dispatch(updateOpenAlertDownloadReport(true));
	};

	const onChangeFilterType = (newValue: string) => {
		dispatch(updateFilterType(newValue));
		const copy = structuredClone(filterUseCustomSearch);
		copy.type = newValue;
		copy.value = "";
		dispatch(updateFilterUseCustomSearch(copy));
	};
	// ************************************************ */
	// USE EFFECT ************************************* */
	useEffect(() => {
		onClickResetFilters();
	}, []);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div className='generalStyles__spaceBetween'>
			<div className='generalStyles__flex'>
				{/** INITIAL DATE */}
				<div>
					<ReportsFilterStartDate showDatePicker={showDatePicker} />
				</div>
				{/** END DATE */}
				<div
					style={{
						marginLeft: `${showDatePicker ? "10px" : "0px"}`,
					}}
				>
					<ReportsFilterEndDate showDatePicker={showDatePicker} />
				</div>
				<div className='generalStyles__flex'>
					{filterUseCustomSearch.showSelect && (
						<div
							style={{
								width: "200px",
								marginLeft: `${
									filterUseCustomSearch.showSelect ? "10px" : "0px"
								}`,
							}}
						>
							<Select
								showSearch
								optionFilterProp='children'
								className='generalStyles__width100'
								allowClear
								value={filterType}
								onChange={onChangeFilterType}
							>
								{["Organizations", "Servers", "Banks", "Lifts"].map(
									(item: string) => (
										<Option key={item} value={item}>
											{item}
										</Option>
									)
								)}
							</Select>
						</div>
					)}
					<div
						style={{
							width: "200px",
							marginLeft: `${showDatePicker ? "10px" : "0px"}`,
						}}
					>
						{filterType === "Servers" && (
							<ReportsFilterGetServers getDataWhenOpen={getDataWhenOpen} />
						)}
						{filterType === "Banks" && (
							<ReportsFilterGetBanks getDataWhenOpen={getDataWhenOpen} />
						)}
						{filterType === "Organizations" && (
							<ReportsFilterGetOrganizations
								getDataWhenOpen={getDataWhenOpen}
							/>
						)}
						{filterType === "Lifts" && (
							<ReportsFilterGetLifts getDataWhenOpen={getDataWhenOpen} />
						)}
					</div>
				</div>
				{/** REPORT TYPE INFORMATION */}
				<div>
					<Tooltip title={t(`${infoDescriptionText}`)}>
						<FontAwesomeIcon
							icon={faInfoCircle}
							className='generalStyles__info'
							style={{ fontSize: "23px", marginLeft: "20px", marginTop: "5px" }}
						/>
					</Tooltip>
				</div>
			</div>
			<div className='generalStyles__flex'>
				{/** SEARCH */}
				<div className='generalStyles__mlFix'>
					<Tooltip
						placement='topLeft'
						title={
							disableSearchAndRefreshButton()
								? t("report.requiredSelectFiler")
								: t("general.search")
						}
					>
						<Button
							className={
								disableSearchAndRefreshButton()
									? "buttonStyle__inactive__1__dark"
									: "buttonStyle__6"
							}
							icon={<FontAwesomeIcon icon={faSearch} />}
							onClick={() => {
								onClickReloadPage();
							}}
							disabled={disableSearchAndRefreshButton()}
						/>
					</Tooltip>
				</div>
				{/** DOWNLOAD */}
				{downloadReport && (
					<div className='generalStyles__mlFix'>
						<Tooltip
							placement='topLeft'
							title={`${t("general.download")} ${t("general.report")}`}
						>
							<Button
								className={
									disableDownloadButton()
										? "buttonStyle__inactive__1__dark"
										: "buttonStyle__8"
								}
								icon={<FontAwesomeIcon icon={faDownload} />}
								onClick={onClickDownloadReport}
								disabled={disableDownloadButton()}
							/>
						</Tooltip>
					</div>
				)}
				{/** OPEN FILTER DRAWER */}
				<div className='generalStyles__mlFix'>
					<Tooltip placement='topLeft' title={t("general.advanceFilters")}>
						<Button
							className='buttonStyle__6'
							icon={<FontAwesomeIcon icon={faFilter} />}
							onClick={() => {
								dispatch(updateOpenDrawerFilters(true));
							}}
						/>
					</Tooltip>
				</div>
				{/** CLEAR FILTERS */}
				<div className='generalStyles__mlFix'>
					<Tooltip placement='topLeft' title={t("general.clearFilters")}>
						<Button
							className='buttonStyle__2'
							icon={<FontAwesomeIcon icon={faFilterCircleXmark} />}
							onClick={onClickResetFilters}
						/>
					</Tooltip>
				</div>
			</div>
		</div>
	);
}

export default ReportsHeader;
